import React, { useContext, useMemo } from "react";
import { FieldPath, FieldValues, useController } from "react-hook-form";
import { GroupBase } from "react-select";
import { loadStripe } from "@stripe/stripe-js";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  Button,
  Divider,
  Icons,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { useGetStripeClientSecret } from "../../../apis/billingApi.hooks";
import { PaymentMethod } from "../../../apis/billingApi";
import { AppContext } from "../../../context/app";
import useToggle from "../../../hooks/state";
import PaymentModal from "../../billing/PaymentModal";
import CadPaymentModal from "../../billing/CADPaymentModal";
import {
  useGetPaymentMethodsQuery,
  PaymentMethodControllerOptionFragment,
} from "./UserBillingSelect-generated.hooks";
import {
  formatSavedPaymentLabel,
  CASH_OPTION,
  COMP_OPTION,
  CASH_UUID,
  COMP_UUID,
} from "./UserBillingSelect.utils";

interface UserBillingSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends PaymentMethodControllerOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends ReactSelectControllerProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > {
  userUuid?: string;
}

function UserBillingSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends PaymentMethodControllerOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: UserBillingSelectProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const { userUuid, control, name, ...other } = props;

  const { client, stripeConfig } = useContext(AppContext);

  const [paymentModalVisible, paymentModalActions] = useToggle(false);

  const [internationalModalVisible, internationalModalActions] =
    useToggle(false);

  const [cadModalVisible, cadModalActions] = useToggle(false);

  const {
    field: { onChange },
  } = useController({ control, name });

  const { data: stripeOptions, refetch: refetchStripeOptions } =
    useGetStripeClientSecret(client?.uuid, userUuid!, {
      enabled: !!client?.uuid && !!userUuid,
    });

  const { data, updateQuery } = useGetPaymentMethodsQuery({
    variables: {
      getPaymentMethodsInput: {
        userUuid: userUuid!,
      },
    },
    skip: !userUuid,
  });

  const stripePromise = useMemo(() => {
    return loadStripe(stripeConfig.publishableKey);
  }, [stripeConfig.publishableKey]);

  const handleAddCard = () => {
    paymentModalActions.on();
  };

  const handleAddBank = () => {
    const isCanada = client!.currencyIso === "CAD";
    if (isCanada) {
      cadModalActions.on();
    } else {
      internationalModalActions.on();
    }
  };

  const handleCreate = (paymentMethod: PaymentMethod) => {
    refetchStripeOptions();
    const newPayment: PaymentMethodControllerOptionFragment = {
      __typename: "PaymentMethod",
      uuid: paymentMethod.uuid,
      billingType: paymentMethod.billingType,
      billingLastFour: paymentMethod.billingLastFour,
    };
    updateQuery((prev) => {
      const newPaymentMethods = [
        ...(prev?.getPaymentMethods || []),
        newPayment,
      ];

      return {
        getPaymentMethods: newPaymentMethods,
      };
    });
    onChange(newPayment);
  };

  const customerDetail = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) => customerDetail.stripeAccountType === "platform",
    );
  }, [stripeOptions]);

  const internationalCustomerDetail = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) => customerDetail.stripeAccountType === "connect",
    );
  }, [stripeOptions]);

  const internationalStripePromise = useMemo(() => {
    if (internationalCustomerDetail) {
      return loadStripe(internationalCustomerDetail?.stripePublishableKey!);
    }
  }, [internationalCustomerDetail]);

  const options = useMemo<PaymentMethodControllerOptionFragment[]>(() => {
    const paymentOptions: PaymentMethodControllerOptionFragment[] = [
      ...(data?.getPaymentMethods || []),
    ];

    paymentOptions.push(CASH_OPTION);
    paymentOptions.push(COMP_OPTION);

    return paymentOptions;
  }, [data]);

  const getOptionLabel = (option: PaymentMethodControllerOptionFragment) => {
    if (option.uuid === CASH_UUID || option.uuid === COMP_UUID) {
      return option.billingType;
    }

    return formatSavedPaymentLabel(option);
  };

  const getOptionValue = (option: PaymentMethodControllerOptionFragment) => {
    return option.uuid;
  };

  const isInternational = !!internationalCustomerDetail;

  const addPaymentButton = (
    <>
      <Divider />
      <div style={{ padding: "4px 12px" }}>
        <Button
          text="Card"
          buttonType="text"
          icon={Icons.Plus}
          iconLocation="left"
          onClick={handleAddCard}
        />
        {isInternational && (
          <Button
            text="Bank"
            buttonType="text"
            icon={Icons.Plus}
            iconLocation="left"
            onClick={handleAddBank}
          />
        )}
      </div>
    </>
  );

  return (
    <>
      <ReactSelectController
        {...other}
        control={control}
        name={name}
        menuPortalTarget={document.body}
        options={options}
        disabled={!userUuid}
        menuAdornment={addPaymentButton}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
      />
      {customerDetail && (
        <PaymentModal
          stripe={stripePromise}
          stripeClientSecret={customerDetail?.clientSecret}
          stripeCustomer={customerDetail?.stripeCustomer}
          userUuid={userUuid!}
          visible={paymentModalVisible}
          title="Add Card"
          onCreate={handleCreate}
          closeModal={paymentModalActions.off}
        />
      )}
      {internationalCustomerDetail && (
        <>
          <PaymentModal
            stripe={internationalStripePromise!}
            stripeClientSecret={internationalCustomerDetail?.clientSecret}
            stripeCustomer={internationalCustomerDetail?.stripeCustomer}
            userUuid={userUuid!}
            visible={internationalModalVisible}
            title="Add Bank"
            onCreate={handleCreate}
            closeModal={internationalModalActions.off}
          />
          <CadPaymentModal
            stripe={internationalStripePromise!}
            stripeClientSecret={internationalCustomerDetail?.clientSecret}
            stripeCustomer={internationalCustomerDetail?.stripeCustomer}
            userUuid={userUuid!}
            visible={cadModalVisible}
            onCreate={handleCreate}
            closeModal={cadModalActions.off}
          />
        </>
      )}
    </>
  );
}

export default UserBillingSelect;
